import clsx from "clsx";
import { getAbsenceTypeLabel } from "@/lib/employeeAbsences";
import type { AbsenceType } from "@/lib/employeeAbsences";
import {
  PROJECT_WORKFLOW_STATUS_LABELS,
  PROJECT_WORKFLOW_STATUSES,
} from "@/lib/projects";
import { clipboardSummary, type PlanungAssignmentClipboard } from "./assignmentClipboard";
import { bookedWorkflowFillClass, type BookedColorMode } from "./bookedAssignments";

const ABSENCE_COLORS: Record<AbsenceType, string> = {
  urlaub: "#0a84ff",
  krank: "#ff375f",
  kindkrank: "#ff9f0a",
  ueberstundenabbau: "#bf5af2",
  sonderurlaub: "#30b0c7",
  schulung: "#30d158",
  unbezahlt: "#8e8e93",
  nicht_verfuegbar: "#8e8e93",
};

export interface PlanningLegendProps {
  clipboard: PlanungAssignmentClipboard | null;
  canEdit: boolean;
  canViewPendingAbsences: boolean;
  showMonteurProposals?: boolean;
  canCreateMonteurProposal?: boolean;
  showBookedToggle?: boolean;
  showPlannedAssignments?: boolean;
  onTogglePlannedAssignments?: () => void;
  showBookedAssignments?: boolean;
  bookedColorMode?: BookedColorMode;
  onToggleBookedAssignments?: () => void;
  onBookedColorModeChange?: (mode: BookedColorMode) => void;
  /** Beim Einfügen mit dem kopierten Termin verknüpfen. */
  pasteLinked?: boolean;
  onTogglePasteLinked?: () => void;
}

export function PlanningLegend({
  clipboard,
  canEdit,
  canViewPendingAbsences,
  showMonteurProposals = false,
  canCreateMonteurProposal = false,
  showBookedToggle = false,
  showPlannedAssignments = true,
  onTogglePlannedAssignments,
  showBookedAssignments = false,
  bookedColorMode = "status",
  onToggleBookedAssignments,
  onBookedColorModeChange,
  pasteLinked = false,
  onTogglePasteLinked,
}: PlanningLegendProps) {
  return (
    <div className="mp-legend-items">
      <button
        type="button"
        className={clsx("mp-booked-switch", showPlannedAssignments && "is-on")}
        role="switch"
        aria-checked={showPlannedAssignments}
        onClick={onTogglePlannedAssignments}
      >
        <span className="mp-booked-switch-track" aria-hidden>
          <span className="mp-booked-switch-knob" />
        </span>
        Geplante Einsätze
      </button>
      {showBookedToggle ? (
        <button
          type="button"
          className={clsx("mp-booked-switch", showBookedAssignments && "is-on")}
          role="switch"
          aria-checked={showBookedAssignments}
          onClick={onToggleBookedAssignments}
        >
          <span className="mp-booked-switch-track" aria-hidden>
            <span className="mp-booked-switch-knob" />
          </span>
          Gebuchte Einsätze
        </button>
      ) : null}
      {showBookedToggle && showBookedAssignments ? (
        <span className="mp-booked-mode" role="group" aria-label="Darstellung gebuchter Einsätze">
          <button
            type="button"
            className={clsx("mp-booked-mode-btn", bookedColorMode === "plan" && "is-on")}
            aria-pressed={bookedColorMode === "plan"}
            onClick={() => onBookedColorModeChange?.("plan")}
          >
            Planung
          </button>
          <button
            type="button"
            className={clsx("mp-booked-mode-btn", bookedColorMode === "status" && "is-on")}
            aria-pressed={bookedColorMode === "status"}
            onClick={() => onBookedColorModeChange?.("status")}
          >
            Zustand
          </button>
        </span>
      ) : null}
      {clipboard && canEdit && (
        <>
          <span className="mp-legend-chip mp-legend-chip--accent">
            Kopiert: {clipboardSummary(clipboard)} — Rechtsklick auf den Zieltag zum Einfügen
          </span>
          {onTogglePasteLinked && (
            <button
              type="button"
              className={clsx("mp-booked-switch mp-link-switch", pasteLinked && "is-on")}
              role="switch"
              aria-checked={pasteLinked}
              title={
                clipboard?.is_series && pasteLinked
                  ? "Serie: der neue Termin bleibt verbunden. Projekte werden nicht mitkopiert und gelten nur für diesen Einsatz."
                  : pasteLinked
                    ? "Eingefügte Termine bleiben verknüpft. Projekte und Feinplanung bleiben synchron – z. B. für eine Montage über ein oder zwei Wochen."
                    : "Eingefügte Termine sind eigenständig. Projekte dieser Woche ändern ältere oder spätere Kopien nicht."
              }
              onClick={onTogglePasteLinked}
            >
              <span className="mp-booked-switch-track" aria-hidden>
                <span className="mp-booked-switch-knob" />
              </span>
              Verknüpft einfügen
            </button>
          )}
        </>
      )}
      {(Object.keys(ABSENCE_COLORS) as AbsenceType[])
        .filter((t) => t !== "nicht_verfuegbar")
        .map((t) => (
        <span key={t} className="mp-legend-item">
          <span
            className="mp-legend-swatch"
            style={{ backgroundColor: ABSENCE_COLORS[t] }}
          />
          {getAbsenceTypeLabel(t)}
        </span>
      ))}
      <span className="mp-legend-item">
        <span className="mp-legend-swatch mp-legend-swatch--credited" />
        Urlaub (krankgeschrieben)
      </span>
      <span className="mp-legend-item">
        <span className="mp-legend-swatch mp-legend-swatch--half" />
        Halber Tag (VM / NM)
      </span>
      <span className="mp-legend-item">
        <span className="mp-legend-swatch mp-legend-weekend" />
        Kein Arbeitstag / nicht verfügbar
      </span>
      <span className="mp-legend-item">
        <span className="mp-legend-swatch mp-legend-swatch--holiday" />
        Feiertag
      </span>
      <span className="mp-legend-item">
        <span className="mp-legend-swatch mp-legend-swatch--bridge" />
        Brückentag
      </span>
      <span className="mp-legend-item">
        <span className="mp-legend-swatch mp-legend-swatch--betriebsferien" />
        Betriebsferien
      </span>
      <span className="mp-legend-item">
        <span className="mp-legend-fixed" aria-hidden>
          !
        </span>
        Fix
      </span>
      <span className="mp-legend-item">
        <span className="mp-legend-swatch mp-legend-swatch--blocker" />
        Blocker (Einsatz)
      </span>
      <span className="mp-legend-item">
        <span className="mp-legend-swatch mp-legend-swatch--company-event" />
        Betriebsveranstaltung
      </span>
      <span className="mp-legend-item">
        <span className="mp-legend-swatch mp-legend-swatch--pending" />
        {canViewPendingAbsences
          ? "Beantragt (wartet auf Freigabe)"
          : "Krankheit beantragt (wartet auf Freigabe)"}
      </span>
      {showMonteurProposals && (
        <span className="mp-legend-item">
          <span className="mp-legend-swatch mp-legend-swatch--monteur-proposal" />
          Terminantrag (Monteur)
        </span>
      )}
      {canCreateMonteurProposal && (
        <span className="mp-legend-chip">
          Mitarbeiter- oder Ressourcenzeile anklicken: Termin oder Ressource beantragen, auch ohne eigene Teilnahme
        </span>
      )}
      {showBookedAssignments && bookedColorMode === "plan" && (
        <>
          <span className="mp-legend-item">
            <span className="mp-legend-swatch mp-legend-swatch--booked" />
            Gebucht laut Planung
          </span>
          <span className="mp-legend-item">
            <span className="mp-legend-swatch mp-legend-swatch--booked-deviates" />
            Abweichung von der Planung
          </span>
        </>
      )}
      {showBookedAssignments && bookedColorMode === "status"
        ? PROJECT_WORKFLOW_STATUSES.map((status) => (
            <span key={status} className="mp-legend-item">
              <span
                className={clsx("mp-legend-swatch", bookedWorkflowFillClass(status))}
              />
              {PROJECT_WORKFLOW_STATUS_LABELS[status]}
            </span>
          ))
        : null}
      {showBookedAssignments && (
        <span className="mp-legend-item">
          <span className="mp-legend-paid" aria-hidden>
            ✓
          </span>
          Vollständig bezahlt
        </span>
      )}
    </div>
  );
}
